<script setup lang="ts">
import axios from 'axios'
import { VDateInput } from 'vuetify/labs/VDateInput'
import { notifyError } from '@/utils/helpers'
import type { ECOption } from '@/plugins/echarts'

definePage({
    meta: {
        id: 'capteurs.temphumidite',
        title: 'Temp. & Humidité',
        icon: 'mdi-thermometer',
        drawerIndex: 8,
        requiresAuth: true,
        requiresSiteAccess: 'agri.capteurs'
    },
})

const TB_BASE_URL = import.meta.env.VITE_TB_URL
const theme = useTheme()
const { smAndDown } = useDisplay()
const isLoading = ref(false)

// ── ThingsBoard auth ──────────────────────────────────────────────────────────

const tbToken = ref<string | null>(null)
const tbAuthority = ref<string | null>(null)
const loginError = ref('')

async function getTbToken(): Promise<string | null> {
    if (tbToken.value) return tbToken.value
    try {
        const response = await apiOmni.post('api/tb/login')
        tbToken.value = response.data.token
        void loadTbAuthority()
        return tbToken.value
    } catch (e: unknown) {
        const err = e as { response?: { status: number; data: unknown }; message?: string }
        loginError.value = err.response
            ? `HTTP ${err.response.status}: ${JSON.stringify(err.response.data)}`
            : (err.message ?? String(e))
        notifyError(loginError.value)
        return null
    }
}

function tbHeaders(token: string): Record<string, string> {
    return {
        'X-Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
        'Accept': 'application/json',
    }
}

// ThingsBoard n'autorise l'écriture d'entité qu'aux administrateurs de tenant : sans ce test,
// les utilisateurs client verraient un bouton « Renommer » qui échoue systématiquement en 403.
async function loadTbAuthority() {
    if (!tbToken.value || tbAuthority.value) return
    try {
        const resp = await axios.get(`${TB_BASE_URL}/api/auth/user`, { headers: tbHeaders(tbToken.value) })
        tbAuthority.value = resp.data?.authority ?? null
    } catch {
        tbAuthority.value = null
    }
}

const peutRenommer = computed(() => tbAuthority.value === 'TENANT_ADMIN')
const peutModifierAlarmes = computed(
    () => tbAuthority.value === 'TENANT_ADMIN' || tbAuthority.value === 'CUSTOMER_USER',
)

// ── Types ─────────────────────────────────────────────────────────────────────

declare interface TbDeviceId {
    id: string
    entityType: string
}

declare interface TbDevice {
    id: TbDeviceId
    name: string
    type: string
    label: string
    deviceProfileId: { id: string; entityType: string }
    createdTime: number
    additionalInfo?: Record<string, unknown>
}

declare interface TbPageData<T> {
    data: T[]
    totalPages: number
    totalElements: number
    hasNext: boolean
}

declare interface TbRelation {
    from: { entityType: string; id: string }
    to: { entityType: string; id: string }
    type: string
}

declare interface TbAsset {
    id: { entityType: string; id: string }
    name: string
    label: string
    type: string
}

declare interface AssetGroup {
    id: string
    name: string
    label: string
    type: string
    devices: TbDevice[]
}

// Les valeurs existantes sont hétérogènes (hysteresis parfois texte « 0,5 », bornes absentes) :
// tout est optionnel et les clés inconnues sont préservées telles quelles à l'enregistrement.
declare interface AlarmMinMaxAttr {
    enabled?: boolean
    min?: number | string | null
    max?: number | string | null
    hysteresis?: number | string | null
    severity?: string
    sendSMS?: boolean
    sendPushover?: boolean
    [key: string]: unknown
}

declare interface AlarmInactivityAttr {
    enabled?: boolean
    sendSMS?: boolean
    sendPushover?: boolean
    [key: string]: unknown
}

declare interface DeviceAttributes {
    alarmTempMinMax: AlarmMinMaxAttr | null
    alarmHumidityMinMax: AlarmMinMaxAttr | null
    alarmInactivity: AlarmInactivityAttr | null
    inactivityTimeout: number | null
    alarmActiveCount: number | null
    active: boolean | null
    lastActivityTime: number | null
    smsPhoneNumbers: string | null
    application_name: string | null
    dev_eui: string | null
}

declare interface TbAlarm {
    id: { entityType: string; id: string }
    type: string
    name: string
    severity: 'CRITICAL' | 'MAJOR' | 'MINOR' | 'WARNING' | 'INDETERMINATE'
    status: string
    acknowledged: boolean
    cleared: boolean
    startTs: number
    endTs: number | null
    clearTs: number | null
}

declare interface S31BTelemetry {
    temperature: number | null
    humidity: number | null
    battery: number | null
    rssi: number | null
    snr: number | null
    ts: number | null
}

// ── State ─────────────────────────────────────────────────────────────────────

const DEVICE_TYPE = 'S31B-LB'

const allDevices = ref<TbDevice[]>([])
const assetGroups = ref<AssetGroup[]>([])
const ungroupedDevices = ref<TbDevice[]>([])
const selectedDevice = ref<TbDevice | null>(null)
const activeId = ref<string | null>(null)
const deviceSearch = ref('')
const openedGroups = ref<string[]>([])

const telemetry = ref<S31BTelemetry>({
    temperature: null,
    humidity: null,
    battery: null,
    rssi: null,
    snr: null,
    ts: null,
})
const isLoadingTelemetry = ref(false)

const emptyAttrs = (): DeviceAttributes => ({
    alarmTempMinMax: null,
    alarmHumidityMinMax: null,
    alarmInactivity: null,
    inactivityTimeout: null,
    alarmActiveCount: null,
    active: null,
    lastActivityTime: null,
    smsPhoneNumbers: null,
    application_name: null,
    dev_eui: null,
})

const attrs = ref<DeviceAttributes>(emptyAttrs())
const isLoadingAttributes = ref(false)
const alarms = ref<TbAlarm[]>([])
const isLoadingAlarms = ref(false)

// ── Filtered tree ─────────────────────────────────────────────────────────────

const filteredGroups = computed<AssetGroup[]>(() => {
    const q = deviceSearch.value.trim().toLowerCase()
    if (!q) return assetGroups.value
    return assetGroups.value
        .map(g => ({
            ...g,
            devices: g.devices.filter(d =>
                (d.label || d.name).toLowerCase().includes(q) ||
                d.name.toLowerCase().includes(q),
            ),
        }))
        .filter(g => g.devices.length > 0 || g.name.toLowerCase().includes(q) || (g.label || '').toLowerCase().includes(q))
})

const filteredUngrouped = computed<TbDevice[]>(() => {
    const q = deviceSearch.value.trim().toLowerCase()
    if (!q) return ungroupedDevices.value
    return ungroupedDevices.value.filter(d =>
        (d.label || d.name).toLowerCase().includes(q) ||
        d.name.toLowerCase().includes(q),
    )
})

// ── Duration helper ───────────────────────────────────────────────────────────

// Certains capteurs stockent leurs seuils en texte, parfois avec une virgule décimale.
function toNumber(value: unknown): number | null {
    if (value === null || value === undefined || value === '') return null
    if (typeof value === 'number') return Number.isFinite(value) ? value : null
    const parsed = parseFloat(String(value).replace(',', '.'))
    return Number.isFinite(parsed) ? parsed : null
}

function formatDuration(ms: number): string {
    const minutes = Math.floor(ms / 60000)
    if (minutes < 60) return `${minutes} min`
    const hours = Math.floor(minutes / 60)
    if (hours < 24) return `${hours} h ${minutes % 60} min`
    const days = Math.floor(hours / 24)
    return `${days} j ${hours % 24} h`
}

const timeSinceLastTelemetry = computed(() =>
    telemetry.value.ts ? formatDuration(Date.now() - telemetry.value.ts) : null,
)

// ── Telemetry fetch ───────────────────────────────────────────────────────────

async function loadTelemetry(deviceId: string) {
    isLoadingTelemetry.value = true
    telemetry.value = { temperature: null, humidity: null, battery: null, rssi: null, snr: null, ts: null }
    try {
        const token = await getTbToken()
        if (!token) return
        const url =
            `${TB_BASE_URL}/api/plugins/telemetry/DEVICE/${deviceId}/values/timeseries` +
            `?keys=data_TempC_SHT,data_Hum_SHT,data_BatV,rssi,snr`
        const resp = await axios.get(url, { headers: tbHeaders(token) })
        const d = resp.data
        telemetry.value = {
            temperature: d?.data_TempC_SHT?.[0]?.value !== undefined ? parseFloat(d.data_TempC_SHT[0].value) : null,
            humidity: d?.data_Hum_SHT?.[0]?.value !== undefined ? parseFloat(d.data_Hum_SHT[0].value) : null,
            battery: d?.data_BatV?.[0]?.value !== undefined ? parseFloat(d.data_BatV[0].value) : null,
            rssi: d?.rssi?.[0]?.value !== undefined ? parseFloat(d.rssi[0].value) : null,
            snr: d?.snr?.[0]?.value !== undefined ? parseFloat(d.snr[0].value) : null,
            ts: d?.data_TempC_SHT?.[0]?.ts ?? d?.data_Hum_SHT?.[0]?.ts ?? null,
        }
    } catch (e) {
        notifyError(e)
    } finally {
        isLoadingTelemetry.value = false
    }
}

// ── Chart state ──────────────────────────────────────────────────────────────

const today = new Date().toISOString().slice(0, 10)
const sevenDaysAgo = new Date(Date.now() - 7 * 86400000).toISOString().slice(0, 10)

const startDate = ref(sevenDaysAgo)
const endDate = ref(today)
const isLoadingChart = ref(false)

function isoJour(d: Date): string {
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

// `v-date-input` en mode plage prend/rend un tableau ; le calendrier ne colore que les jours
// réellement présents, il faut donc lister chaque jour entre les deux bornes.
function plageDepuisBornes(min: string, max: string): Date[] {
    if (!min || !max) return []
    const dates: Date[] = []
    const jour = new Date(`${min}T00:00:00`)
    const fin = new Date(`${max}T00:00:00`)
    while (jour <= fin) {
        dates.push(new Date(jour))
        jour.setDate(jour.getDate() + 1)
    }
    return dates
}

const plageHistorique = computed<Date[]>({
    get: () => plageDepuisBornes(startDate.value, endDate.value),
    set: (dates) => {
        if (dates.length === 0) return
        startDate.value = isoJour(dates[0])
        endDate.value = isoJour(dates[dates.length - 1])
    },
})

function appliquerPériode(jours: number) {
    const fin = new Date()
    const début = new Date()
    début.setDate(début.getDate() - (jours - 1))
    startDate.value = isoJour(début)
    endDate.value = isoJour(fin)
    loadChart()
}

declare interface ChartPoint { ts: number; value: number }

const tempPoints = ref<ChartPoint[]>([])
const humPoints = ref<ChartPoint[]>([])

declare interface Seuils { min: number | null; max: number | null }

function seuilsActifs(attr: AlarmMinMaxAttr | null): Seuils | null {
    if (!attr?.enabled) return null
    const min = toNumber(attr.min)
    const max = toNumber(attr.max)
    return min === null && max === null ? null : { min, max }
}

const seuilsTemp = computed(() => seuilsActifs(attrs.value.alarmTempMinMax))
const seuilsHum = computed(() => seuilsActifs(attrs.value.alarmHumidityMinMax))

const COULEUR_TEMP = '#2196F3'
const COULEUR_HUM = '#26C281'
const COULEUR_ALARME = '#EF4444'

function markLineSeuils(seuils: Seuils | null, unit: string, couleur: string) {
    if (!seuils) return undefined
    const ligne = (valeur: number, texte: string) => ({
        yAxis: valeur,
        name: texte,
        lineStyle: { color: couleur, type: 'dashed' as const, width: 1.5, opacity: 0.9 },
    })
    const data = []
    if (seuils.min !== null) data.push(ligne(seuils.min, `Min ${seuils.min} ${unit}`))
    if (seuils.max !== null) data.push(ligne(seuils.max, `Max ${seuils.max} ${unit}`))
    return {
        silent: true,
        symbol: ['none', 'none'] as [string, string],
        data,
        label: {
            show: true,
            formatter: (p: { name: string }) => p.name,
            position: 'insideEndTop' as const,
            color: couleur,
            fontSize: 10,
            fontWeight: 'bold' as const,
        },
    }
}

const estSombre = computed(() => theme.global.name.value === 'dark')
const couleurTexte = computed(() => (estSombre.value ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.6)'))
const couleurGrille = computed(() => (estSombre.value ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.07)'))

// ECharts accepte la forme littérale d'un dégradé : pas besoin d'importer `graphic.LinearGradient`.
function dégradé(couleur: string, opacité: number) {
    const alpha = Math.round(opacité * 255).toString(16).padStart(2, '0')
    return {
        type: 'linear' as const,
        x: 0, y: 0, x2: 0, y2: 1,
        colorStops: [
            { offset: 0, color: `${couleur}${alpha}` },
            { offset: 1, color: `${couleur}00` },
        ],
    }
}

const chartOption = computed<ECOption>(() => {
    if (tempPoints.value.length === 0 && humPoints.value.length === 0) return {}
    const axeCommun = {
        axisLine: { show: false },
        axisTick: { show: false },
        axisLabel: { color: couleurTexte.value, fontSize: 10 },
        nameTextStyle: { color: couleurTexte.value, fontSize: 10 },
    }
    return {
        backgroundColor: 'transparent',
        tooltip: {
            trigger: 'axis',
            axisPointer: { type: 'line', lineStyle: { color: couleurTexte.value, type: 'dashed' } },
            backgroundColor: estSombre.value ? 'rgba(30,30,30,0.94)' : 'rgba(255,255,255,0.96)',
            borderWidth: 0,
            padding: [8, 12],
            textStyle: { color: estSombre.value ? '#fff' : '#000', fontSize: 12 },
            formatter(params: unknown) {
                const items = params as Array<{ seriesName: string; value: [number, number]; marker: string }>
                if (!items.length) return ''
                const ts = items[0].value[0]
                const date = new Date(ts).toLocaleString('fr-CA', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
                let html = `<div style="opacity:.7;margin-bottom:4px;">${date}</div>`
                for (const p of items) {
                    const isHum = p.seriesName === 'Humidité'
                    const unit = isHum ? ' %' : ' °C'
                    html += `${p.marker}${p.seriesName} <b style="float:right;margin-left:16px;">${p.value[1] !== null ? p.value[1].toFixed(1) : '—'}${unit}</b><br/>`
                }
                return html
            },
        },
        legend: {
            top: 0,
            right: 8,
            icon: 'roundRect',
            itemWidth: 14,
            itemHeight: 4,
            textStyle: { color: couleurTexte.value, fontSize: 11 },
        },
        grid: { top: 34, left: 8, right: 8, bottom: 4, containLabel: true },
        xAxis: {
            type: 'time',
            ...axeCommun,
            splitLine: { show: true, lineStyle: { color: couleurGrille.value, type: 'dashed' } },
        },
        yAxis: [
            {
                type: 'value',
                name: '°C',
                position: 'left',
                scale: true,
                ...axeCommun,
                splitLine: { show: true, lineStyle: { color: couleurGrille.value } },
            },
            {
                type: 'value',
                name: '%',
                position: 'right',
                min: 0,
                max: 100,
                ...axeCommun,
                splitLine: { show: false },
            },
        ],
        dataZoom: [{ type: 'inside', zoomOnMouseWheel: 'shift', moveOnMouseWheel: false }],
        series: [
            {
                name: 'Température',
                type: 'line',
                yAxisIndex: 0,
                showSymbol: false,
                smooth: 0.2,
                data: tempPoints.value.map(p => [p.ts, p.value]),
                itemStyle: { color: COULEUR_TEMP },
                lineStyle: { width: 2, color: COULEUR_TEMP },
                areaStyle: { color: dégradé(COULEUR_TEMP, 0.28) },
                markLine: markLineSeuils(seuilsTemp.value, '°C', COULEUR_ALARME),
            },
            {
                name: 'Humidité',
                type: 'line',
                yAxisIndex: 1,
                showSymbol: false,
                smooth: 0.2,
                data: humPoints.value.map(p => [p.ts, p.value]),
                itemStyle: { color: COULEUR_HUM },
                lineStyle: { width: 1.5, color: COULEUR_HUM, type: 'dashed' },
                markLine: markLineSeuils(seuilsHum.value, '%', COULEUR_HUM),
            },
        ],
    }
})

async function loadChart() {
    if (!selectedDevice.value) return
    isLoadingChart.value = true
    tempPoints.value = []
    humPoints.value = []
    try {
        const token = await getTbToken()
        if (!token) return
        const startTs = new Date(startDate.value).getTime()
        const endTs = new Date(endDate.value).getTime() + 86400000
        const url =
            `${TB_BASE_URL}/api/plugins/telemetry/DEVICE/${selectedDevice.value.id.id}/values/timeseries` +
            `?keys=data_TempC_SHT,data_Hum_SHT&startTs=${startTs}&endTs=${endTs}&limit=50000&orderBy=ASC`
        const resp = await axios.get(url, { headers: tbHeaders(token) })
        tempPoints.value = (resp.data?.data_TempC_SHT ?? []).map((p: { ts: number; value: string }) => ({ ts: p.ts, value: parseFloat(p.value) }))
        humPoints.value = (resp.data?.data_Hum_SHT ?? []).map((p: { ts: number; value: string }) => ({ ts: p.ts, value: parseFloat(p.value) }))
    } catch (e) {
        notifyError(e)
    } finally {
        isLoadingChart.value = false
    }
}

// ── Diagnostics chart dialog ─────────────────────────────────────────────────

type DiagMetric = 'battery' | 'rssi' | 'snr'

const diagDialog = ref(false)
const diagMetric = ref<DiagMetric>('battery')
const diagPoints = ref<ChartPoint[]>([])
const diagStartDate = ref('')
const diagEndDate = ref('')
const isLoadingDiag = ref(false)

const diagMeta = computed(() => {
    switch (diagMetric.value) {
        case 'battery': return { key: 'data_BatV', label: 'Tension batterie', unit: 'V', color: '#4CAF50', icon: 'mdi-battery-medium' }
        case 'rssi':    return { key: 'rssi',      label: 'RSSI',             unit: 'dBm', color: '#2196F3', icon: 'mdi-signal-cellular-2' }
        case 'snr':     return { key: 'snr',       label: 'SNR',              unit: 'dB',  color: '#9C27B0', icon: 'mdi-sine-wave' }
    }
})

const diagChartOption = computed<ECOption>(() => {
    if (!diagPoints.value.length) return {}
    const meta = diagMeta.value
    return {
        backgroundColor: 'transparent',
        tooltip: {
            trigger: 'axis',
            formatter(params: unknown) {
                const items = params as Array<{ value: [number, number]; marker: string }>
                if (!items.length) return ''
                const date = new Date(items[0].value[0]).toLocaleString('fr-CA', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
                return `<b>${date}</b><br/>${items[0].marker}${meta.label} : <b>${items[0].value[1].toFixed(2)} ${meta.unit}</b>`
            },
        },
        grid: { top: 24, left: '3%', right: '4%', bottom: '3%', containLabel: true },
        xAxis: { type: 'time' },
        yAxis: { type: 'value', name: meta.unit, splitLine: { show: true } },
        series: [{
            name: meta.label,
            type: 'line',
            showSymbol: false,
            data: diagPoints.value.map(p => [p.ts, p.value]),
            lineStyle: { width: 2, color: meta.color },
            itemStyle: { color: meta.color },
            areaStyle: { opacity: 0.08, color: meta.color },
        }],
    }
})

async function loadDiagChart() {
    if (!selectedDevice.value) return
    isLoadingDiag.value = true
    diagPoints.value = []
    try {
        const token = await getTbToken()
        if (!token) return
        const startTs = new Date(diagStartDate.value).getTime()
        const endTs = new Date(diagEndDate.value).getTime() + 86400000
        const key = diagMeta.value.key
        const url =
            `${TB_BASE_URL}/api/plugins/telemetry/DEVICE/${selectedDevice.value.id.id}/values/timeseries` +
            `?keys=${key}&startTs=${startTs}&endTs=${endTs}&limit=50000&orderBy=ASC`
        const resp = await axios.get(url, { headers: tbHeaders(token) })
        diagPoints.value = (resp.data?.[key] ?? []).map((p: { ts: number; value: string }) => ({ ts: p.ts, value: parseFloat(p.value) }))
    } catch (e) {
        notifyError(e)
    } finally {
        isLoadingDiag.value = false
    }
}

function openDiagChart(metric: DiagMetric) {
    diagMetric.value = metric
    diagStartDate.value = new Date(Date.now() - 31 * 86400000).toISOString().slice(0, 10)
    diagEndDate.value = new Date().toISOString().slice(0, 10)
    diagDialog.value = true
    loadDiagChart()
}

async function loadAttributes(deviceId: string) {
    isLoadingAttributes.value = true
    try {
        const token = await getTbToken()
        if (!token) return
        const resp = await axios.get(
            `${TB_BASE_URL}/api/plugins/telemetry/DEVICE/${deviceId}/values/attributes`,
            { headers: tbHeaders(token) },
        )
        const raw = resp.data as Array<{ key: string; value: unknown }>
        const get = (key: string) => raw.find(r => r.key === key)?.value
        attrs.value = {
            alarmTempMinMax: (get('alarmTempMinMax') as AlarmMinMaxAttr) ?? null,
            alarmHumidityMinMax: (get('alarmHumidityMinMax') as AlarmMinMaxAttr) ?? null,
            alarmInactivity: (get('alarmInactivity') as AlarmInactivityAttr) ?? null,
            inactivityTimeout: toNumber(get('inactivityTimeout')),
            alarmActiveCount: (get('alarmActiveCount') as number) ?? null,
            active: (get('active') as boolean) ?? null,
            lastActivityTime: (get('lastActivityTime') as number) ?? null,
            smsPhoneNumbers: (get('smsPhoneNumbers') as string) ?? null,
            application_name: (get('application_name') as string) ?? null,
            dev_eui: (get('dev_eui') as string) ?? null,
        }
    } catch (e) {
        notifyError(e)
    } finally {
        isLoadingAttributes.value = false
    }
}

async function loadAlarms(deviceId: string) {
    isLoadingAlarms.value = true
    try {
        const token = await getTbToken()
        if (!token) return
        const resp = await axios.get(
            `${TB_BASE_URL}/api/alarm/DEVICE/${deviceId}?pageSize=20&page=0`,
            { headers: tbHeaders(token) },
        )
        alarms.value = resp.data?.data ?? []
    } catch (e) {
        notifyError(e)
    } finally {
        isLoadingAlarms.value = false
    }
}

// ── Alarm helpers ─────────────────────────────────────────────────────────────

const activeAlarmsCount = computed(() => alarms.value.filter(a => !a.cleared).length)

function alarmSeverityColor(severity: string): string {
    switch (severity) {
        case 'CRITICAL': return 'red-darken-4'
        case 'MAJOR': return 'error'
        case 'MINOR': return 'warning'
        case 'WARNING': return 'orange'
        default: return 'secondary'
    }
}

function alarmStatusColor(status: string): string {
    return status.startsWith('ACTIVE') ? 'error' : 'success'
}

function alarmStatusLabel(status: string): string {
    switch (status) {
        case 'ACTIVE_UNACK': return 'Active'
        case 'ACTIVE_ACK': return 'Active (ack.)'
        case 'CLEARED_UNACK': return 'Résolue'
        case 'CLEARED_ACK': return 'Résolue (ack.)'
        default: return status
    }
}

// ── Device selection ──────────────────────────────────────────────────────────

function onSelectDevice(device: TbDevice) {
    activeId.value = device.id.id
    selectedDevice.value = device
    pickerOpen.value = false
    mobileTab.value = 'mesures'
    tempPoints.value = []
    humPoints.value = []
    alarms.value = []
    attrs.value = emptyAttrs()
    loadTelemetry(device.id.id)
    loadChart()
    loadAttributes(device.id.id)
    loadAlarms(device.id.id)
}

// ── Mobile navigation ─────────────────────────────────────────────────────────

type MobileTab = 'mesures' | 'alertes'

const mobileTab = ref<MobileTab>('mesures')
const pickerOpen = ref(false)

declare interface PickerRow {
    kind: 'header' | 'device'
    key: string
    title: string
    device?: TbDevice
}

// Le sélecteur mobile est une liste plate : les groupes deviennent de simples en-têtes,
// ce qui évite d'avoir à replier/déplier des sections sur un petit écran.
const pickerRows = computed<PickerRow[]>(() => {
    const rows: PickerRow[] = []
    for (const group of filteredGroups.value) {
        rows.push({ kind: 'header', key: `g-${group.id}`, title: group.label || group.name })
        for (const device of group.devices)
            rows.push({ kind: 'device', key: device.id.id, title: device.label || device.name, device })
    }
    if (filteredUngrouped.value.length > 0) {
        if (filteredGroups.value.length > 0)
            rows.push({ kind: 'header', key: 'g-none', title: 'Sans groupe' })
        for (const device of filteredUngrouped.value)
            rows.push({ kind: 'device', key: device.id.id, title: device.label || device.name, device })
    }
    return rows
})


// ── Load devices + build tree ─────────────────────────────────────────────────

async function fetchAllDevices(token: string): Promise<TbDevice[]> {
    const PAGE_SIZE = 100
    let page = 0
    const result: TbDevice[]= []
    while (true) {
        const url =
            `${TB_BASE_URL}/api/tenant/devices` +
            `?pageSize=${PAGE_SIZE}&page=${page}&type=${encodeURIComponent(DEVICE_TYPE)}`
        const response = await axios.get<TbPageData<TbDevice>>(url, { headers: tbHeaders(token) })
        result.push(...response.data.data)
        if (!response.data.hasNext) break
        page++
    }
    return result.sort((a, b) => (a.label || a.name).localeCompare(b.label || b.name))
}

async function fetchParentAsset(token: string, deviceId: string): Promise<TbAsset | null> {
    try {
        const rels = await axios.get<TbRelation[]>(
            `${TB_BASE_URL}/api/relations?toId=${deviceId}&toType=DEVICE`,
            { headers: tbHeaders(token) },
        )
        const assetRel = rels.data.find(r => r.from.entityType === 'ASSET')
        if (!assetRel) return null
        const asset = await axios.get<TbAsset>(
            `${TB_BASE_URL}/api/asset/${assetRel.from.id}`,
            { headers: tbHeaders(token) },
        )
        return asset.data
    } catch {
        return null
    }
}

async function loadDevices() {
    loginError.value = ''
    isLoading.value = true
    selectedDevice.value = null
    activeId.value = null
    assetGroups.value = []
    ungroupedDevices.value = []
    openedGroups.value = []

    try {
        const token = await getTbToken()
        if (!token) return

        const devices = await fetchAllDevices(token)
        allDevices.value = devices

        // Fetch parent assets in parallel (batches of 10 to avoid flooding)
        const BATCH = 10
        const assetMap = new Map<string, TbAsset>()  // assetId -> asset
        const deviceAssetMap = new Map<string, string>() // deviceId -> assetId

        for (let i = 0; i < devices.length; i += BATCH) {
            const batch = devices.slice(i, i + BATCH)
            const results = await Promise.all(batch.map(d => fetchParentAsset(token, d.id.id)))
            results.forEach((asset, idx) => {
                if (asset) {
                    assetMap.set(asset.id.id, asset)
                    deviceAssetMap.set(batch[idx].id.id, asset.id.id)
                }
            })
        }

        // Build grouped structure
        const groupMap = new Map<string, AssetGroup>()
        const ungroup: TbDevice[] = []

        for (const device of devices) {
            const assetId = deviceAssetMap.get(device.id.id)
            if (assetId) {
                const asset = assetMap.get(assetId)!
                if (!groupMap.has(assetId)) {
                    groupMap.set(assetId, {
                        id: assetId,
                        name: asset.name,
                        label: asset.label,
                        type: asset.type,
                        devices: [],
                    })
                }
                groupMap.get(assetId)!.devices.push(device)
            } else {
                ungroup.push(device)
            }
        }

        assetGroups.value = Array.from(groupMap.values())
            .sort((a, b) => (a.label || a.name).localeCompare(b.label || b.name))
        ungroupedDevices.value = ungroup

        // Open all groups by default
        openedGroups.value = assetGroups.value.map(g => g.id)
    } catch (e) {
        notifyError(e)
    } finally {
        isLoading.value = false
    }
}

onMounted(loadDevices)

// ── Renommer un capteur ───────────────────────────────────────────────────────

const renameDialog = ref(false)
const renameLabel = ref('')
const isSavingLabel = ref(false)

function openRenameDialog() {
    if (!selectedDevice.value) return
    renameLabel.value = selectedDevice.value.label || ''
    renameDialog.value = true
}

async function saveLabel() {
    const device = selectedDevice.value
    if (!device) return
    const label = renameLabel.value.trim()
    isSavingLabel.value = true
    try {
        const token = await getTbToken()
        if (!token) return
        // ThingsBoard remplace l'entité complète : on repart de la version courante du serveur
        // pour ne pas écraser les champs modifiés ailleurs entre-temps.
        const current = await axios.get<TbDevice>(
            `${TB_BASE_URL}/api/device/${device.id.id}`,
            { headers: tbHeaders(token) },
        )
        const resp = await axios.post<TbDevice>(
            `${TB_BASE_URL}/api/device`,
            { ...current.data, label },
            { headers: tbHeaders(token) },
        )
        device.label = resp.data.label
        renameDialog.value = false
        Notify.success('Nom du capteur modifié avec succès')
    } catch (e) {
        notifyError(e)
    } finally {
        isSavingLabel.value = false
    }
}

// ── Réglages d'alarme ─────────────────────────────────────────────────────────

declare interface AlarmMinMaxForm {
    enabled: boolean
    min: number | null
    max: number | null
    hysteresis: number | null
    sendSMS: boolean
    sendPushover: boolean
}

declare interface AlarmForm {
    temp: AlarmMinMaxForm
    hum: AlarmMinMaxForm
    inactivityEnabled: boolean
    inactivitySendSMS: boolean
    inactivitySendPushover: boolean
    inactivityTimeoutMin: number | null
}

const alarmDialog = ref(false)
const isSavingAlarms = ref(false)

function toMinMaxForm(attr: AlarmMinMaxAttr | null): AlarmMinMaxForm {
    return {
        enabled: attr?.enabled ?? false,
        min: toNumber(attr?.min),
        max: toNumber(attr?.max),
        hysteresis: toNumber(attr?.hysteresis),
        // Côté règle ThingsBoard, sendSMS absent équivaut à « oui » sauf sévérité MINOR.
        sendSMS: attr?.sendSMS ?? (attr?.severity !== 'MINOR'),
        sendPushover: attr?.sendPushover ?? false,
    }
}

const alarmForm = ref<AlarmForm>({
    temp: toMinMaxForm(null),
    hum: toMinMaxForm(null),
    inactivityEnabled: false,
    inactivitySendSMS: true,
    inactivitySendPushover: false,
    inactivityTimeoutMin: null,
})

function openAlarmDialog() {
    const inactivity = attrs.value.alarmInactivity
    alarmForm.value = {
        temp: toMinMaxForm(attrs.value.alarmTempMinMax),
        hum: toMinMaxForm(attrs.value.alarmHumidityMinMax),
        inactivityEnabled: inactivity?.enabled ?? false,
        inactivitySendSMS: inactivity?.sendSMS ?? true,
        inactivitySendPushover: inactivity?.sendPushover ?? false,
        inactivityTimeoutMin: attrs.value.inactivityTimeout !== null
            ? Math.round(attrs.value.inactivityTimeout / 60000)
            : null,
    }
    alarmDialog.value = true
}

const alarmFormErreur = computed<string | null>(() => {
    const f = alarmForm.value
    if (f.temp.enabled && f.temp.min === null && f.temp.max === null)
        return 'Précisez au moins une borne de température.'
    if (f.hum.enabled && f.hum.min === null && f.hum.max === null)
        return 'Précisez au moins une borne d\'humidité.'
    if (f.temp.min !== null && f.temp.max !== null && f.temp.min >= f.temp.max)
        return 'Le minimum de température doit être inférieur au maximum.'
    if (f.hum.min !== null && f.hum.max !== null && f.hum.min >= f.hum.max)
        return 'Le minimum d\'humidité doit être inférieur au maximum.'
    if (f.inactivityEnabled && (f.inactivityTimeoutMin ?? 0) <= 0)
        return 'Le délai d\'inactivité doit être supérieur à 0 minute.'
    return null
})

// Les clés non exposées (severity, messages personnalisés…) sont conservées telles quelles.
function buildMinMaxAttr(previous: AlarmMinMaxAttr | null, form: AlarmMinMaxForm): AlarmMinMaxAttr {
    return {
        ...(previous ?? {}),
        enabled: form.enabled,
        min: form.min,
        max: form.max,
        hysteresis: form.hysteresis,
        sendSMS: form.sendSMS,
        sendPushover: form.sendPushover,
    }
}

async function saveAlarms() {
    const device = selectedDevice.value
    if (!device || alarmFormErreur.value) return
    isSavingAlarms.value = true
    try {
        const token = await getTbToken()
        if (!token) return
        const f = alarmForm.value
        await axios.post(
            `${TB_BASE_URL}/api/plugins/telemetry/DEVICE/${device.id.id}/attributes/SHARED_SCOPE`,
            {
                alarmTempMinMax: buildMinMaxAttr(attrs.value.alarmTempMinMax, f.temp),
                alarmHumidityMinMax: buildMinMaxAttr(attrs.value.alarmHumidityMinMax, f.hum),
                alarmInactivity: {
                    ...(attrs.value.alarmInactivity ?? {}),
                    enabled: f.inactivityEnabled,
                    sendSMS: f.inactivitySendSMS,
                    sendPushover: f.inactivitySendPushover,
                },
            },
            { headers: tbHeaders(token) },
        )

        const timeoutMs = f.inactivityTimeoutMin !== null ? f.inactivityTimeoutMin * 60000 : null
        if (timeoutMs !== null && timeoutMs !== attrs.value.inactivityTimeout) {
            await axios.post(
                `${TB_BASE_URL}/api/plugins/telemetry/DEVICE/${device.id.id}/attributes/SERVER_SCOPE`,
                { inactivityTimeout: timeoutMs },
                { headers: tbHeaders(token) },
            )
        }

        alarmDialog.value = false
        Notify.success('Réglages d\'alarme enregistrés avec succès')
        await loadAttributes(device.id.id)
    } catch (e) {
        notifyError(e)
    } finally {
        isSavingAlarms.value = false
    }
}


// ── Display colors ────────────────────────────────────────────────────────────

const tempColor = computed(() => {
    const t = telemetry.value.temperature
    if (t === null) return undefined
    if (t <= 0) return 'rgb(var(--v-theme-info))'
    if (t <= 10) return 'rgb(var(--v-theme-primary))'
    if (t <= 25) return 'rgb(var(--v-theme-success))'
    if (t <= 35) return 'rgb(var(--v-theme-warning))'
    return 'rgb(var(--v-theme-error))'
})

const humidityColor = computed(() => {
    const h = telemetry.value.humidity
    if (h === null) return undefined
    if (h < 30) return 'rgb(var(--v-theme-warning))'
    if (h <= 70) return 'rgb(var(--v-theme-success))'
    return 'rgb(var(--v-theme-info))'
})
</script>

<template>
    <v-card class="page-card" :loading="isLoading">

        <!-- Page header -->
        <!-- Sur mobile il n'y a pas de v-app-bar : on décale à droite pour libérer le hamburger flottant. -->
        <div class="page-header" :class="smAndDown ? 'ps-16 pe-3 py-2' : 'px-5 pt-5 pb-3'">
            <div class="d-flex align-center justify-space-between flex-wrap" style="gap: 12px;">
                <div class="d-flex align-center" style="gap: 12px; min-width: 0;">
                    <v-avatar v-if="!smAndDown" color="primary" variant="tonal" size="44" rounded="lg">
                        <v-icon icon="mdi-thermometer" size="26" />
                    </v-avatar>
                    <div v-if="!smAndDown">
                        <div class="text-h6 font-weight-bold" style="line-height: 1.2;">Température & Humidité</div>
                        <div class="text-caption text-medium-emphasis">Capteurs S31B-LB</div>
                    </div>

                    <!-- Sélecteur de capteur (mobile) -->
                    <v-chip
                        v-else
                        color="primary"
                        variant="tonal"
                        size="large"
                        class="culture-chip chip-capteur"
                        prepend-icon="mdi-thermometer"
                        append-icon="mdi-chevron-down"
                        style="cursor: pointer; font-weight: 600;"
                        @click="pickerOpen = true"
                    >
                        {{ selectedDevice ? (selectedDevice.label || selectedDevice.name) : 'Choisir un capteur' }}
                    </v-chip>
                </div>
                <div class="d-flex align-center" style="gap: 8px;">
                    <v-btn
                        v-if="smAndDown"
                        icon="mdi-refresh"
                        color="primary"
                        variant="tonal"
                        size="small"
                        :loading="isLoading"
                        @click="loadDevices"
                    />
                    <v-btn
                        v-else
                        color="primary" variant="tonal" rounded="pill" prepend-icon="mdi-refresh"
                        :loading="isLoading" @click="loadDevices"
                    >
                        Rafraîchir
                    </v-btn>
                </div>
            </div>
        </div>

        <v-divider />

        <!-- Error banner -->
        <v-alert
            v-if="loginError"
            type="error"
            variant="tonal"
            density="compact"
            class="mx-4 mt-3 mb-0 flex-shrink-0"
            rounded="lg"
        >
            {{ loginError }}
        </v-alert>

        <!-- Main content: tree + detail panel -->
        <div class="d-flex" style="flex: 1 1 auto; overflow: hidden; min-height: 0;">

            <!-- Left: device tree (desktop) -->
            <div v-if="!smAndDown" class="tree-panel d-flex flex-column">

                <!-- Search -->
                <div class="pa-3">
                    <v-text-field
                        v-model="deviceSearch"
                        label="Rechercher un capteur"
                        density="compact"
                        variant="outlined"
                        rounded="lg"
                        hide-details
                        clearable
                        prepend-inner-icon="mdi-magnify"
                    />
                </div>

                <v-divider />

                <!-- Tree -->
                <div style="flex: 1 1 auto; overflow-y: auto;">
                    <v-list
                        density="compact"
                        nav
                        open-strategy="multiple"
                        :opened="openedGroups"
                        @update:opened="openedGroups = $event"
                    >
                        <!-- Asset groups -->
                        <v-list-group
                            v-for="group in filteredGroups"
                            :key="group.id"
                            :value="group.id"
                        >
                            <template #activator="{ props }">
                                <v-list-item
                                    v-bind="props"
                                    density="compact"
                                    rounded="lg"
                                >
                                    <template #prepend>
                                        <v-icon icon="mdi-warehouse" size="16" color="secondary" class="mr-2" />
                                    </template>
                                    <v-list-item-title class="text-caption font-weight-bold">
                                        {{ group.label || group.name }}
                                    </v-list-item-title>
                                    <v-list-item-subtitle v-if="group.label" class="text-caption">
                                        {{ group.name }}
                                    </v-list-item-subtitle>
                                    <template #append>
                                        <v-chip size="x-small" variant="tonal" color="secondary" class="ml-1">
                                            {{ group.devices.length }}
                                        </v-chip>
                                    </template>
                                </v-list-item>
                            </template>

                            <v-list-item
                                v-for="device in group.devices"
                                :key="device.id.id"
                                density="compact"
                                rounded="lg"
                                :active="activeId === device.id.id"
                                color="primary"
                                class="pl-6"
                                @click="onSelectDevice(device)"
                            >
                                <template #prepend>
                                    <v-icon icon="mdi-thermometer" size="14" color="primary" class="mr-2" />
                                </template>
                                <v-list-item-title class="text-caption font-weight-medium">
                                    {{ device.label || device.name }}
                                </v-list-item-title>
                                <v-list-item-subtitle v-if="device.label" class="text-caption">
                                    {{ device.name }}
                                </v-list-item-subtitle>
                            </v-list-item>
                        </v-list-group>

                        <!-- Ungrouped devices -->
                        <template v-if="filteredUngrouped.length > 0">
                            <v-list-subheader v-if="filteredGroups.length > 0" class="text-caption">
                                Sans groupe
                            </v-list-subheader>
                            <v-list-item
                                v-for="device in filteredUngrouped"
                                :key="device.id.id"
                                density="compact"
                                rounded="lg"
                                :active="activeId === device.id.id"
                                color="primary"
                                @click="onSelectDevice(device)"
                            >
                                <template #prepend>
                                    <v-icon icon="mdi-thermometer" size="14" color="primary" class="mr-2" />
                                </template>
                                <v-list-item-title class="text-caption font-weight-medium">
                                    {{ device.label || device.name }}
                                </v-list-item-title>
                                <v-list-item-subtitle v-if="device.label" class="text-caption">
                                    {{ device.name }}
                                </v-list-item-subtitle>
                            </v-list-item>
                        </template>
                    </v-list>

                    <div
                        v-if="!isLoading && filteredGroups.length === 0 && filteredUngrouped.length === 0 && !loginError"
                        class="pa-4 text-center text-medium-emphasis text-caption"
                    >
                        Aucun capteur trouvé
                    </div>
                </div>
            </div>

            <!-- Right: device detail -->
            <div class="detail-panel">
                <!-- Placeholder -->
                <template v-if="!selectedDevice">
                    <div
                        class="d-flex flex-column align-center justify-center text-medium-emphasis pa-4"
                        style="height: 100%; gap: 12px;"
                    >
                        <v-icon icon="mdi-gesture-tap" size="48" />
                        <span class="text-body-2 text-center">
                            {{ smAndDown ? 'Touchez « Choisir un capteur » ci-dessus' : 'Sélectionnez un capteur dans la liste' }}
                        </span>
                        <span v-if="!isLoading && allDevices.length === 0 && !loginError" class="text-body-2 text-center">
                            Contactez-nous pour avoir ces capteurs sur votre ferme !
                            <a href="https://www.agrimetrie.com/capteur-de-temperature-et-dhumidite" target="_blank" rel="noopener noreferrer">
                                Cliquez ici pour plus d'informations.
                            </a>
                        </span>
                    </div>
                </template>

                <!-- Detail -->
                <template v-else>
                    <!-- Onglets (mobile) : évite un défilement interminable sur petit écran -->
                    <!-- flex-grow-0 : `.v-slide-group` est en `flex: 1 0 auto` et s'étirerait sur toute la hauteur. -->
                    <v-tabs
                        v-if="smAndDown"
                        v-model="mobileTab"
                        color="primary"
                        density="compact"
                        grow
                        class="flex-grow-0 flex-shrink-0"
                    >
                        <v-tab value="mesures" prepend-icon="mdi-gauge" text="Mesures" />
                        <v-tab value="alertes" prepend-icon="mdi-bell-outline" text="Alertes" />
                    </v-tabs>

                    <div class="detail-scroll" :class="{ 'detail-scroll-mobile': smAndDown }">

                    <!-- Device header card -->
                    <v-card
                        v-show="!smAndDown || mobileTab === 'mesures'"
                        variant="outlined" rounded="lg" class="flex-shrink-0"
                    >
                        <v-card-title class="d-flex align-center flex-wrap" style="gap: 10px;">
                            <v-avatar color="primary" variant="tonal" size="36" rounded="lg">
                                <v-icon icon="mdi-thermometer" size="20" />
                            </v-avatar>
                            <div style="flex: 1 1 140px; min-width: 0;">
                                <div class="text-subtitle-1 font-weight-bold text-truncate">
                                    {{ selectedDevice.label || selectedDevice.name }}
                                </div>
                                <div v-if="selectedDevice.label" class="text-caption text-medium-emphasis text-truncate">
                                    {{ selectedDevice.name }}
                                </div>
                                <div v-if="attrs.application_name" class="text-caption text-medium-emphasis text-truncate" style="font-style: italic;">
                                    {{ attrs.application_name }}
                                </div>
                            </div>
                            <!-- Diagnostics chips -->
                            <div v-if="!isLoadingTelemetry" class="d-flex align-center flex-wrap" style="gap: 6px;">
                                <v-chip
                                    v-if="attrs.alarmActiveCount !== null && attrs.alarmActiveCount > 0"
                                    size="x-small"
                                    color="error"
                                    variant="flat"
                                    prepend-icon="mdi-bell-ring"
                                >
                                    {{ attrs.alarmActiveCount }} alarme{{ attrs.alarmActiveCount > 1 ? 's' : '' }}
                                </v-chip>
                                <v-chip
                                    v-if="telemetry.battery !== null"
                                    size="x-small"
                                    :color="telemetry.battery >= 3.5 ? 'success' : telemetry.battery >= 3.2 ? 'warning' : 'error'"
                                    variant="tonal"
                                    :prepend-icon="telemetry.battery >= 3.5 ? 'mdi-battery-high' : telemetry.battery >= 3.2 ? 'mdi-battery-medium' : 'mdi-battery-low'"
                                    @click="openDiagChart('battery')"
                                >
                                    {{ telemetry.battery.toFixed(2) }} V
                                </v-chip>
                                <v-chip
                                    v-if="telemetry.rssi !== null"
                                    size="x-small"
                                    :color="telemetry.rssi >= -70 ? 'success' : telemetry.rssi >= -85 ? 'warning' : 'error'"
                                    variant="tonal"
                                    :prepend-icon="telemetry.rssi >= -70 ? 'mdi-signal-cellular-3' : telemetry.rssi >= -85 ? 'mdi-signal-cellular-2' : 'mdi-signal-cellular-1'"
                                    @click="openDiagChart('rssi')"
                                >
                                    {{ telemetry.rssi }} dBm
                                </v-chip>
                                <v-chip
                                    v-if="telemetry.snr !== null"
                                    size="x-small"
                                    color="secondary"
                                    variant="tonal"
                                    prepend-icon="mdi-sine-wave"
                                    @click="openDiagChart('snr')"
                                >
                                    {{ telemetry.snr.toFixed(1) }} dB
                                </v-chip>
                            </div>
                            <v-progress-circular v-else size="16" width="2" indeterminate color="primary" />

                            <!-- Actions d'édition -->
                            <v-menu v-if="peutRenommer || peutModifierAlarmes">
                                <template #activator="{ props }">
                                    <v-btn v-bind="props" icon="mdi-dots-vertical" variant="text" size="small" />
                                </template>
                                <v-list density="compact">
                                    <v-list-item
                                        v-if="peutRenommer"
                                        prepend-icon="mdi-pencil-outline"
                                        title="Renommer le capteur"
                                        @click="openRenameDialog"
                                    />
                                    <v-list-item
                                        v-if="peutModifierAlarmes"
                                        prepend-icon="mdi-bell-cog-outline"
                                        title="Réglages d'alarme"
                                        :disabled="isLoadingAttributes"
                                        @click="openAlarmDialog"
                                    />
                                </v-list>
                            </v-menu>
                        </v-card-title>

                        <v-divider />

                        <v-card-text class="pa-3">
                            <!-- Type chip -->
                            <div class="mb-3">
                                <v-chip size="small" color="primary" variant="tonal" prepend-icon="mdi-tag-outline">
                                    {{ selectedDevice.type || '—' }}
                                </v-chip>
                            </div>

                            <!-- Latest telemetry -->
                            <div v-if="isLoadingTelemetry" class="d-flex align-center" style="gap: 8px;">
                                <v-progress-circular size="16" width="2" indeterminate color="primary" />
                                <span class="text-caption text-medium-emphasis">Chargement...</span>
                            </div>

                            <div v-else class="d-flex flex-wrap align-start" style="gap: 16px;">

                                <!-- Temperature -->
                                <div class="sensor-stat">
                                    <div class="text-caption text-medium-emphasis">Température</div>
                                    <div class="text-h5 font-weight-bold d-flex align-end" style="gap: 4px; line-height: 1.1;">
                                        <span :style="{ color: tempColor }">
                                            {{ telemetry.temperature !== null ? telemetry.temperature.toFixed(1) : '—' }}
                                        </span>
                                        <span v-if="telemetry.temperature !== null" class="text-body-2 text-medium-emphasis pb-1">°C</span>
                                    </div>
                                </div>

                                <v-divider vertical style="height: 48px; align-self: center;" />

                                <!-- Humidity -->
                                <div class="sensor-stat">
                                    <div class="text-caption text-medium-emphasis">Humidité relative</div>
                                    <div class="text-h5 font-weight-bold d-flex align-end" style="gap: 4px; line-height: 1.1;">
                                        <span :style="{ color: humidityColor }">
                                            {{ telemetry.humidity !== null ? telemetry.humidity.toFixed(1) : '—' }}
                                        </span>
                                        <span v-if="telemetry.humidity !== null" class="text-body-2 text-medium-emphasis pb-1">%</span>
                                    </div>
                                </div>

                                <v-divider vertical style="height: 48px; align-self: center;" />

                                <!-- Last seen -->
                                <div class="sensor-stat">
                                    <div class="text-caption text-medium-emphasis">Dernière donnée</div>
                                    <div class="text-body-2 font-weight-bold">
                                        {{ timeSinceLastTelemetry ?? '—' }}
                                    </div>
                                    <div v-if="telemetry.ts" class="text-caption text-medium-emphasis">
                                        {{ new Date(telemetry.ts).toLocaleString('fr-CA') }}
                                    </div>
                                </div>
                            </div>

                            <!-- Alarm thresholds -->
                            <template v-if="!isLoadingAttributes && (attrs.alarmTempMinMax || attrs.alarmHumidityMinMax)">
                                <v-divider class="my-3" />
                                <div class="d-flex align-center flex-wrap" style="gap: 6px;">
                                    <span class="text-caption text-medium-emphasis mr-1">
                                        <v-icon size="12" icon="mdi-bell-outline" class="mr-1" />Seuils :
                                    </span>
                                    <template v-if="attrs.alarmTempMinMax">
                                        <v-chip v-if="toNumber(attrs.alarmTempMinMax.min) !== null" size="x-small" :color="attrs.alarmTempMinMax.enabled ? 'info' : 'default'" variant="tonal" prepend-icon="mdi-thermometer-low">
                                            {{ toNumber(attrs.alarmTempMinMax.min) }} °C
                                        </v-chip>
                                        <v-chip v-if="toNumber(attrs.alarmTempMinMax.max) !== null" size="x-small" :color="attrs.alarmTempMinMax.enabled ? 'error' : 'default'" variant="tonal" prepend-icon="mdi-thermometer-high">
                                            {{ toNumber(attrs.alarmTempMinMax.max) }} °C
                                        </v-chip>
                                    </template>
                                    <v-divider v-if="attrs.alarmTempMinMax && attrs.alarmHumidityMinMax" vertical style="height: 16px;" />
                                    <template v-if="attrs.alarmHumidityMinMax">
                                        <v-chip v-if="toNumber(attrs.alarmHumidityMinMax.min) !== null" size="x-small" :color="attrs.alarmHumidityMinMax.enabled ? 'warning' : 'default'" variant="tonal" prepend-icon="mdi-water-percent">
                                            {{ toNumber(attrs.alarmHumidityMinMax.min) }} %
                                        </v-chip>
                                        <v-chip v-if="toNumber(attrs.alarmHumidityMinMax.max) !== null" size="x-small" :color="attrs.alarmHumidityMinMax.enabled ? 'info' : 'default'" variant="tonal" prepend-icon="mdi-water-percent">
                                            {{ toNumber(attrs.alarmHumidityMinMax.max) }} %
                                        </v-chip>
                                    </template>
                                    <v-chip v-if="attrs.alarmInactivity?.enabled" size="x-small" color="warning" variant="tonal" prepend-icon="mdi-sleep">
                                        Inactivité
                                    </v-chip>
                                </div>
                            </template>
                        </v-card-text>
                    </v-card>

                    <!-- Historical chart -->
                    <v-card
                        v-show="!smAndDown || mobileTab === 'mesures'"
                        variant="outlined" rounded="lg"
                        :class="smAndDown ? 'carte-remplissage' : 'mt-4'"
                    >
                        <v-card-title class="py-2 px-4">
                            <div class="d-flex align-center flex-wrap" style="gap: 10px;">
                                <div v-if="!smAndDown" class="d-flex align-center" style="gap: 8px; flex: 0 0 auto;">
                                    <v-icon icon="mdi-chart-line" size="18" />
                                    <span class="text-subtitle-2">Historique</span>
                                </div>
                                <v-date-input
                                    v-model="plageHistorique"
                                    label="Période"
                                    multiple="range"
                                    variant="outlined"
                                    density="compact"
                                    rounded="lg"
                                    hide-details
                                    autocomplete="off"
                                    prepend-icon=""
                                    prepend-inner-icon="mdi-calendar-range-outline"
                                    :picker-props="{ color: 'primary' }"
                                    style="min-width: 190px; flex: 1 1 190px; max-width: 300px;"
                                    @update:model-value="loadChart()"
                                />
                                <div class="d-flex align-center" style="gap: 4px;">
                                    <v-btn size="x-small" variant="tonal" rounded="pill" @click="appliquerPériode(7)">7 j</v-btn>
                                    <v-btn size="x-small" variant="tonal" rounded="pill" @click="appliquerPériode(30)">30 j</v-btn>
                                </div>
                                <v-spacer v-if="!smAndDown" />
                                <v-btn
                                    icon="mdi-refresh"
                                    variant="text"
                                    size="small"
                                    :loading="isLoadingChart"
                                    @click="loadChart"
                                />
                            </div>
                        </v-card-title>
                        <v-divider />
                        <v-card-text class="pa-2">
                            <div v-if="isLoadingChart" class="d-flex align-center justify-center chart-zone">
                                <v-progress-circular indeterminate color="primary" />
                            </div>
                            <div
                                v-else-if="tempPoints.length === 0 && humPoints.length === 0"
                                class="d-flex align-center justify-center text-medium-emphasis text-caption chart-zone"
                            >
                                Aucune donnée sur la période sélectionnée
                            </div>
                            <VChart
                                v-else
                                :option="chartOption"
                                autoresize
                                class="chart-zone"
                                style="width: 100%;"
                            />
                        </v-card-text>
                    </v-card>

                    <!-- Alarm history -->
                    <v-card
                        v-show="!smAndDown || mobileTab === 'alertes'"
                        variant="outlined" rounded="lg"
                        :class="smAndDown ? '' : 'mt-4'"
                    >
                        <v-card-title class="py-2 px-4">
                            <div class="d-flex align-center" style="gap: 8px;">
                                <v-icon icon="mdi-bell-outline" size="18" />
                                <span class="text-subtitle-2">Alertes récentes</span>
                                <v-chip v-if="activeAlarmsCount > 0" size="x-small" color="error" variant="flat" class="ml-1">
                                    {{ activeAlarmsCount }} active{{ activeAlarmsCount > 1 ? 's' : '' }}
                                </v-chip>
                                <v-spacer />
                                <span class="text-caption text-medium-emphasis">20 dernières</span>
                            </div>
                        </v-card-title>
                        <v-divider />
                        <v-card-text class="pa-0">
                            <div v-if="isLoadingAlarms" class="d-flex align-center justify-center pa-6">
                                <v-progress-circular size="20" indeterminate color="primary" />
                            </div>
                            <div v-else-if="alarms.length === 0" class="pa-4 text-center text-caption text-medium-emphasis">
                                Aucune alerte enregistrée
                            </div>
                            <v-list v-else density="compact" class="pa-0">
                                <template v-for="(alarm, idx) in alarms" :key="idx">
                                    <v-list-item density="compact" class="px-4 py-2">
                                        <template #prepend>
                                            <v-icon :color="alarmSeverityColor(alarm.severity)" size="16" icon="mdi-bell" class="mr-2" />
                                        </template>
                                        <v-list-item-title class="text-caption font-weight-medium">
                                            {{ alarm.name || alarm.type }}
                                        </v-list-item-title>
                                        <v-list-item-subtitle class="text-caption">
                                            {{ new Date(alarm.startTs).toLocaleString('fr-CA', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) }}
                                            <template v-if="alarm.clearTs">
                                                &rarr; {{ new Date(alarm.clearTs).toLocaleString('fr-CA', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) }}
                                            </template>
                                        </v-list-item-subtitle>
                                        <!-- Sur mobile les puces passent sous le texte plutôt que d'écraser le titre -->
                                        <div v-if="smAndDown" class="d-flex align-center flex-wrap mt-1" style="gap: 4px;">
                                            <v-chip size="x-small" :color="alarmSeverityColor(alarm.severity)" variant="tonal">
                                                {{ alarm.severity }}
                                            </v-chip>
                                            <v-chip size="x-small" :color="alarmStatusColor(alarm.status)" variant="tonal">
                                                {{ alarmStatusLabel(alarm.status) }}
                                            </v-chip>
                                        </div>
                                        <template #append>
                                            <div v-if="!smAndDown" class="d-flex align-center" style="gap: 4px;">
                                                <v-chip size="x-small" :color="alarmSeverityColor(alarm.severity)" variant="tonal">
                                                    {{ alarm.severity }}
                                                </v-chip>
                                                <v-chip size="x-small" :color="alarmStatusColor(alarm.status)" variant="tonal">
                                                    {{ alarmStatusLabel(alarm.status) }}
                                                </v-chip>
                                            </div>
                                        </template>
                                    </v-list-item>
                                    <v-divider v-if="idx < alarms.length - 1" />
                                </template>
                            </v-list>
                        </v-card-text>
                    </v-card>
                    </div>
                </template>
            </div>
        </div>
    </v-card>

    <!-- Sélecteur de capteur (mobile) -->
    <v-dialog v-model="pickerOpen" fullscreen scrollable transition="dialog-bottom-transition">
        <v-card>
            <v-card-title class="d-flex align-center pt-4 px-4" style="gap: 10px;">
                <v-icon icon="mdi-thermometer" size="20" color="primary" />
                <span class="text-subtitle-1 font-weight-bold">Capteurs</span>
                <v-spacer />
                <v-btn icon="mdi-close" variant="text" size="small" @click="pickerOpen = false" />
            </v-card-title>
            <div class="px-4 pb-3">
                <v-text-field
                    v-model="deviceSearch"
                    label="Rechercher un capteur"
                    density="compact"
                    variant="outlined"
                    rounded="lg"
                    hide-details
                    clearable
                    autofocus
                    prepend-inner-icon="mdi-magnify"
                />
            </div>
            <v-divider />
            <v-card-text class="pa-0">
                <v-list density="comfortable" nav>
                    <template v-for="row in pickerRows" :key="row.key">
                        <v-list-subheader v-if="row.kind === 'header'" class="text-caption font-weight-bold">
                            {{ row.title }}
                        </v-list-subheader>
                        <v-list-item
                            v-else
                            rounded="lg"
                            :active="activeId === row.key"
                            color="primary"
                            @click="onSelectDevice(row.device!)"
                        >
                            <template #prepend>
                                <v-icon icon="mdi-thermometer" size="18" color="primary" class="mr-2" />
                            </template>
                            <v-list-item-title class="text-body-2 font-weight-medium">
                                {{ row.title }}
                            </v-list-item-title>
                            <v-list-item-subtitle class="text-caption">
                                {{ row.device!.name }}
                            </v-list-item-subtitle>
                        </v-list-item>
                    </template>
                </v-list>
                <div v-if="pickerRows.length === 0" class="pa-6 text-center text-medium-emphasis text-caption">
                    Aucun capteur trouvé
                </div>
            </v-card-text>
        </v-card>
    </v-dialog>

    <!-- Renommer un capteur -->
    <v-dialog v-model="renameDialog" max-width="480" :fullscreen="smAndDown">
        <v-card rounded="xl">
            <v-card-title class="d-flex align-center pt-4 px-5" style="gap: 10px;">
                <v-icon icon="mdi-pencil-outline" size="20" color="primary" />
                <span class="text-subtitle-1 font-weight-bold">Renommer le capteur</span>
                <v-spacer />
                <v-btn icon="mdi-close" variant="text" size="small" @click="renameDialog = false" />
            </v-card-title>
            <v-divider />
            <v-card-text class="pt-4">
                <v-text-field
                    v-model="renameLabel"
                    label="Nom affiché"
                    variant="outlined"
                    density="comfortable"
                    rounded="lg"
                    autofocus
                    counter="255"
                    :hint="`Identifiant technique : ${selectedDevice?.name ?? ''}`"
                    persistent-hint
                    @keyup.enter="saveLabel"
                />
            </v-card-text>
            <v-card-actions class="px-5 pb-4">
                <v-spacer />
                <v-btn variant="text" rounded="pill" @click="renameDialog = false">Annuler</v-btn>
                <v-btn
                    color="primary" variant="flat" rounded="pill"
                    :loading="isSavingLabel"
                    :disabled="renameLabel.trim().length === 0"
                    @click="saveLabel"
                >
                    Enregistrer
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>

    <!-- Réglages d'alarme -->
    <v-dialog v-model="alarmDialog" max-width="620" :fullscreen="smAndDown" scrollable>
        <v-card rounded="xl">
            <v-card-title class="d-flex align-center pt-4 px-5" style="gap: 10px;">
                <v-icon icon="mdi-bell-cog-outline" size="20" color="primary" />
                <span class="text-subtitle-1 font-weight-bold">Réglages d'alarme</span>
                <v-spacer />
                <v-btn icon="mdi-close" variant="text" size="small" @click="alarmDialog = false" />
            </v-card-title>
            <v-divider />

            <v-card-text class="pt-4">
                <!-- Température -->
                <div class="d-flex align-center mb-2" style="gap: 8px;">
                    <v-icon icon="mdi-thermometer" size="18" color="primary" />
                    <span class="text-subtitle-2">Température</span>
                    <v-spacer />
                    <v-switch
                        v-model="alarmForm.temp.enabled"
                        color="primary" density="compact" hide-details inset
                    />
                </div>
                <v-row dense>
                    <v-col cols="12" sm="4">
                        <v-text-field
                            v-model.number="alarmForm.temp.min"
                            label="Min (°C)" type="number" step="0.1"
                            variant="outlined" density="compact" rounded="lg" hide-details clearable
                            :disabled="!alarmForm.temp.enabled"
                        />
                    </v-col>
                    <v-col cols="12" sm="4">
                        <v-text-field
                            v-model.number="alarmForm.temp.max"
                            label="Max (°C)" type="number" step="0.1"
                            variant="outlined" density="compact" rounded="lg" hide-details clearable
                            :disabled="!alarmForm.temp.enabled"
                        />
                    </v-col>
                    <v-col cols="12" sm="4">
                        <v-text-field
                            v-model.number="alarmForm.temp.hysteresis"
                            label="Hystérésis (°C)" type="number" step="0.1" min="0"
                            variant="outlined" density="compact" rounded="lg" hide-details clearable
                            placeholder="1,5" persistent-placeholder
                            :disabled="!alarmForm.temp.enabled"
                        />
                    </v-col>
                </v-row>
                <div class="d-flex align-center flex-wrap mt-2" style="gap: 16px;">
                    <v-switch
                        v-model="alarmForm.temp.sendSMS" label="SMS"
                        color="primary" density="compact" hide-details inset
                        :disabled="!alarmForm.temp.enabled"
                    />
                    <v-switch
                        v-model="alarmForm.temp.sendPushover" label="Pushover"
                        color="primary" density="compact" hide-details inset
                        :disabled="!alarmForm.temp.enabled"
                    />
                </div>

                <v-divider class="my-4" />

                <!-- Humidité -->
                <div class="d-flex align-center mb-2" style="gap: 8px;">
                    <v-icon icon="mdi-water-percent" size="18" color="primary" />
                    <span class="text-subtitle-2">Humidité relative</span>
                    <v-spacer />
                    <v-switch
                        v-model="alarmForm.hum.enabled"
                        color="primary" density="compact" hide-details inset
                    />
                </div>
                <v-row dense>
                    <v-col cols="12" sm="4">
                        <v-text-field
                            v-model.number="alarmForm.hum.min"
                            label="Min (%)" type="number" step="1"
                            variant="outlined" density="compact" rounded="lg" hide-details clearable
                            :disabled="!alarmForm.hum.enabled"
                        />
                    </v-col>
                    <v-col cols="12" sm="4">
                        <v-text-field
                            v-model.number="alarmForm.hum.max"
                            label="Max (%)" type="number" step="1"
                            variant="outlined" density="compact" rounded="lg" hide-details clearable
                            :disabled="!alarmForm.hum.enabled"
                        />
                    </v-col>
                    <v-col cols="12" sm="4">
                        <v-text-field
                            v-model.number="alarmForm.hum.hysteresis"
                            label="Hystérésis (%)" type="number" step="0.5" min="0"
                            variant="outlined" density="compact" rounded="lg" hide-details clearable
                            placeholder="1,5" persistent-placeholder
                            :disabled="!alarmForm.hum.enabled"
                        />
                    </v-col>
                </v-row>
                <div class="d-flex align-center flex-wrap mt-2" style="gap: 16px;">
                    <v-switch
                        v-model="alarmForm.hum.sendSMS" label="SMS"
                        color="primary" density="compact" hide-details inset
                        :disabled="!alarmForm.hum.enabled"
                    />
                    <v-switch
                        v-model="alarmForm.hum.sendPushover" label="Pushover"
                        color="primary" density="compact" hide-details inset
                        :disabled="!alarmForm.hum.enabled"
                    />
                </div>

                <v-divider class="my-4" />

                <!-- Inactivité -->
                <div class="d-flex align-center mb-2" style="gap: 8px;">
                    <v-icon icon="mdi-sleep" size="18" color="primary" />
                    <span class="text-subtitle-2">Inactivité</span>
                    <v-spacer />
                    <v-switch
                        v-model="alarmForm.inactivityEnabled"
                        color="primary" density="compact" hide-details inset
                    />
                </div>
                <v-row dense>
                    <v-col cols="12" sm="4">
                        <v-text-field
                            v-model.number="alarmForm.inactivityTimeoutMin"
                            label="Délai (min)" type="number" step="1" min="1"
                            variant="outlined" density="compact" rounded="lg" hide-details
                            :disabled="!alarmForm.inactivityEnabled"
                        />
                    </v-col>
                </v-row>
                <div class="d-flex align-center flex-wrap mt-2" style="gap: 16px;">
                    <v-switch
                        v-model="alarmForm.inactivitySendSMS" label="SMS"
                        color="primary" density="compact" hide-details inset
                        :disabled="!alarmForm.inactivityEnabled"
                    />
                    <v-switch
                        v-model="alarmForm.inactivitySendPushover" label="Pushover"
                        color="primary" density="compact" hide-details inset
                        :disabled="!alarmForm.inactivityEnabled"
                    />
                </div>

                <v-alert
                    v-if="alarmFormErreur"
                    type="warning" variant="tonal" density="compact" rounded="lg" class="mt-4"
                >
                    {{ alarmFormErreur }}
                </v-alert>
            </v-card-text>

            <v-divider />
            <v-card-actions class="px-5 py-3">
                <v-spacer />
                <v-btn variant="text" rounded="pill" @click="alarmDialog = false">Annuler</v-btn>
                <v-btn
                    color="primary" variant="flat" rounded="pill"
                    :loading="isSavingAlarms"
                    :disabled="alarmFormErreur !== null"
                    @click="saveAlarms"
                >
                    Enregistrer
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>

    <!-- Diagnostics chart dialog -->
    <v-dialog v-model="diagDialog" max-width="680" :fullscreen="smAndDown" scrollable>
        <v-card rounded="xl">
            <v-card-title class="d-flex align-center pt-4 px-5" style="gap: 10px;">
                <v-icon :icon="diagMeta.icon" size="20" :color="diagMeta.color" />
                <span class="text-subtitle-1 font-weight-bold">{{ diagMeta.label }}</span>
                <div v-if="selectedDevice" class="text-caption text-medium-emphasis font-weight-regular">
                    — {{ selectedDevice.label || selectedDevice.name }}
                </div>
                <v-spacer />
                <v-btn icon="mdi-close" variant="text" size="small" @click="diagDialog = false" />
            </v-card-title>

            <v-divider />

            <!-- Date range row -->
            <div class="d-flex align-center px-4 pt-3 pb-2 flex-wrap" style="gap: 10px;">
                <v-text-field
                    v-model="diagStartDate"
                    label="Date de début"
                    type="date"
                    variant="outlined"
                    density="compact"
                    rounded="lg"
                    hide-details
                    style="min-width: 150px; flex: 1 1 150px; max-width: 200px;"
                />
                <v-text-field
                    v-model="diagEndDate"
                    label="Date de fin"
                    type="date"
                    variant="outlined"
                    density="compact"
                    rounded="lg"
                    hide-details
                    style="min-width: 150px; flex: 1 1 150px; max-width: 200px;"
                />
                <v-btn
                    color="primary"
                    variant="tonal"
                    rounded="lg"
                    size="small"
                    prepend-icon="mdi-magnify"
                    :loading="isLoadingDiag"
                    @click="loadDiagChart"
                >
                    Afficher
                </v-btn>
            </div>

            <v-card-text class="pa-2">
                <div v-if="isLoadingDiag" class="d-flex align-center justify-center" style="height: 280px;">
                    <v-progress-circular indeterminate color="primary" />
                </div>
                <div
                    v-else-if="diagPoints.length === 0"
                    class="d-flex align-center justify-center text-medium-emphasis text-caption"
                    style="height: 280px;"
                >
                    Aucune donnée sur la période sélectionnée
                </div>
                <VChart
                    v-else
                    :option="diagChartOption"
                    autoresize
                    style="height: 280px; width: 100%;"
                />
            </v-card-text>
        </v-card>
    </v-dialog>
</template>

<style scoped>
/* ── Page header ─────────────────────────── */
.page-header {
    background: v-bind('theme.global.name.value === "dark" ? "rgba(var(--v-theme-surface-variant), 0.25)" : "rgba(var(--v-theme-primary), 0.04)"');
    flex-shrink: 0;
}

/* Comme la page Carte : la carte occupe tout le viewport, sans marge ni coins arrondis. */
.page-card {
    height: calc(100vh - var(--v-layout-top, 64px) - var(--v-layout-bottom, 0px));
    display: flex;
    flex-direction: column;
    border-radius: 0;
}

.culture-chip {
    transition: opacity 0.15s;
}
.culture-chip:hover {
    opacity: 0.78;
}

/* Le nom du capteur peut être long : la puce se tronque au lieu de pousser le bouton hors écran. */
.chip-capteur {
    max-width: 55vw;
}
.chip-capteur :deep(.v-chip__content) {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.tree-panel {
    flex-shrink: 0;
    width: 300px;
    border-right: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
}

.detail-panel {
    flex: 1 1 auto;
    display: flex;
    flex-direction: column;
    min-width: 0;
    min-height: 0;
}

.detail-scroll {
    flex: 1 1 auto;
    overflow-y: auto;
    padding: 16px;
    min-height: 0;
}

/* Sur mobile un seul onglet est visible à la fois : il occupe toute la hauteur restante. */
.detail-scroll-mobile {
    display: flex;
    flex-direction: column;
    padding: 8px;
    gap: 8px;
}

.carte-remplissage {
    flex: 1 1 auto;
    display: flex;
    flex-direction: column;
    min-height: 260px;
}
.carte-remplissage :deep(.v-card-text) {
    flex: 1 1 auto;
    min-height: 0;
}

.chart-zone {
    height: 260px;
}
.carte-remplissage .chart-zone {
    height: 100%;
}

.sensor-stat {
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 80px;
}
</style>
